import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Search,
  Plus,
  MoreVertical,
  Users,
  Folder,
  Globe,
  Lock,
  Edit2,
  Trash2,
} from 'lucide-react';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import {
  fetchWorkspaces,
  setSearchQuery,
  clearSearch,
} from '../../store/workspaceSlice';
import { Workspace, WorkspaceVisibility, WorkspaceMemberRole } from '../../types/common';
import { getWorkspaceRole } from '../../hooks/usePermissions';
import CreateWorkspaceModal from '../../components/Workspace/CreateWorkspaceModal';
import EditWorkspaceModal from '../../components/Workspace/EditWorkspaceModal';
import DeleteWorkspaceModal from '../../components/Workspace/DeleteWorkspaceModal';
import './WorkspaceList.scss';

const WorkspaceList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { filteredWorkspaces = [], loading = false, searchQuery = '' } = useAppSelector(
    (state) => state.workspaces || {}
  );
  const { user } = useAppSelector((state) => state.auth || {});

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuWorkspace, setMenuWorkspace] = useState<Workspace | null>(null);

  useEffect(() => {
    dispatch(fetchWorkspaces());
  }, [dispatch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchQuery(event.target.value));
  };

  const handleClearSearch = () => {
    dispatch(clearSearch());
  };

  const handleWorkspaceClick = (workspace: Workspace) => {
    navigate(`/workspace/${workspace.id}`);
  };

  const handleCreateWorkspace = () => {
    setCreateModalOpen(true);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, workspace: Workspace) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuWorkspace(workspace);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuWorkspace(null);
  };

  const handleEdit = () => {
    if (menuWorkspace) {
      setSelectedWorkspace(menuWorkspace);
      setEditModalOpen(true);
      handleMenuClose();
    }
  };

  const handleDelete = () => {
    if (menuWorkspace) {
      setSelectedWorkspace(menuWorkspace);
      setDeleteModalOpen(true);
      handleMenuClose();
    }
  };

  const getVisibilityIcon = (visibility: WorkspaceVisibility) => {
    switch (visibility) {
      case WorkspaceVisibility.OPEN:
      case WorkspaceVisibility.PUBLIC:
        return <Globe size={16} />;
      case WorkspaceVisibility.HIDDEN:
      case WorkspaceVisibility.PRIVATE:
        return <Lock size={16} />;
      case WorkspaceVisibility.CLOSED:
      case WorkspaceVisibility.TEAM:
        return <Users size={16} />;
      default:
        return <Users size={16} />;
    }
  };

  const getVisibilityColor = (visibility: WorkspaceVisibility) => {
    switch (visibility) {
      case WorkspaceVisibility.OPEN:
      case WorkspaceVisibility.PUBLIC:
        return 'success';
      case WorkspaceVisibility.HIDDEN:
      case WorkspaceVisibility.PRIVATE:
        return 'error';
      case WorkspaceVisibility.CLOSED:
      case WorkspaceVisibility.TEAM:
        return 'primary';
      default:
        return 'default';
    }
  };

  // Only the owner can delete a workspace (matches the backend rule) - a
  // plain ownerId compare isn't quite right once ownership can be
  // transferred, so resolve it the same way the rest of the app does.
  const isOwner = (workspace: Workspace) => {
    return getWorkspaceRole(workspace, user) === WorkspaceMemberRole.OWNER;
  };

  return (
    <Box className="workspace-list-page">
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box className="workspace-list-header">
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              My Workspaces
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage and organize your workspaces
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            onClick={handleCreateWorkspace}
            sx={{ height: 'fit-content' }}
          >
            Create Workspace
          </Button>
        </Box>

        {/* Search Bar */}
        <Box className="workspace-search" sx={{ mt: 4, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search workspaces..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Empty State */}
        {!loading && filteredWorkspaces.length === 0 && searchQuery === '' && (
          <Box className="workspace-empty-state">
            <Folder size={64} strokeWidth={1} />
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              No workspaces yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first workspace to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<Plus size={20} />}
              onClick={handleCreateWorkspace}
            >
              Create Workspace
            </Button>
          </Box>
        )}

        {/* No Search Results */}
        {!loading && filteredWorkspaces.length === 0 && searchQuery !== '' && (
          <Box className="workspace-empty-state">
            <Search size={64} strokeWidth={1} />
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              No workspaces found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Try adjusting your search criteria
            </Typography>
            <Button variant="outlined" onClick={handleClearSearch}>
              Clear Search
            </Button>
          </Box>
        )}

        {/* Workspace Grid */}
        {!loading && filteredWorkspaces.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: 3,
            }}
          >
            {filteredWorkspaces.map((workspace) => (
              <Box key={workspace.id}>
                <Card
                  className="workspace-card"
                  onClick={() => handleWorkspaceClick(workspace)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3,
                    },
                  }}
                >
                  <Box
                    className="workspace-card-header"
                    sx={{
                      height: 8,
                      backgroundColor: workspace.color || '#0073ea',
                    }}
                  />
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6" component="h2" noWrap sx={{ flex: 1 }}>
                        {workspace.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, workspace)}
                        sx={{ ml: 1 }}
                      >
                        <MoreVertical size={18} />
                      </IconButton>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        height: 40,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {workspace.description || 'No description'}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        icon={getVisibilityIcon(workspace.visibility)}
                        label={workspace.visibility}
                        size="small"
                        color={getVisibilityColor(workspace.visibility) as any}
                        variant="outlined"
                      />
                      {isOwner(workspace) && (
                        <Chip label="Owner" size="small" color="primary" />
                      )}
                    </Box>
                  </CardContent>

                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        width: '100%',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Folder size={16} />
                        <Typography variant="caption" color="text.secondary">
                          {workspace.boardCount || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Users size={16} />
                        <Typography variant="caption" color="text.secondary">
                          {workspace.memberCount || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </CardActions>
                </Card>
              </Box>
            ))}
          </Box>
        )}

        {/* Context Menu */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleEdit}>
            <Edit2 size={18} style={{ marginRight: 8 }} />
            Edit Workspace
          </MenuItem>
          {menuWorkspace && isOwner(menuWorkspace) && (
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              <Trash2 size={18} style={{ marginRight: 8 }} />
              Delete Workspace
            </MenuItem>
          )}
        </Menu>

        {/* Modals */}
        <CreateWorkspaceModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} />
        <EditWorkspaceModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          workspace={selectedWorkspace}
        />
        <DeleteWorkspaceModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          workspace={selectedWorkspace}
        />
      </Container>
    </Box>
  );
};

export default WorkspaceList;
